import { TreePine } from "lucide-react";

import { useEditorStore, type LastUsedBrush } from "@/state/useEditorStore";
import { getMapChromeTheme } from "@/components/mapChromeTheme";
import {
  getCatalogDecorationById,
  getTileSetById,
  getTileUrl,
} from "@west-marches/shared";

function ToolButton({
  label,
  active,
  onClick,
  disabled = false,
  disabledTooltip,
  showDisabledCursor = true,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
  showDisabledCursor?: boolean;
  children: React.ReactNode;
}) {
  const theme = getMapChromeTheme();
  const tooltipLabel = disabledTooltip ?? label;

  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-disabled={disabled || undefined}
        className={`rounded-lg p-2 transition-colors ${
          active ? theme.toolButtonActive : theme.toolButtonIdle
        } ${showDisabledCursor ? "disabled:cursor-not-allowed" : ""}`}
      >
        <span className={disabled ? "opacity-40" : undefined}>{children}</span>
      </button>
      <span
        className={`pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 transition-opacity group-hover:opacity-100 ${theme.tooltip}`}
        role="tooltip"
      >
        {tooltipLabel}
      </span>
    </span>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const theme = getMapChromeTheme();

  return (
    <button
      onClick={onClick}
      className={`relative z-10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${
        active ? theme.tabButtonActive : theme.tabButtonIdle
      }`}
    >
      {label}
    </button>
  );
}

function PaintTools({
  onTileBrushClick,
  onDecorationBrushClick,
}: {
  onTileBrushClick: () => void;
  onDecorationBrushClick: () => void;
}) {
  const activeTool = useEditorStore((s) => s.activeTool);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);

  return (
    <div className="flex items-center gap-1">
      <ToolButton
        label="Pointer (1, V)"
        active={activeTool === "pointer"}
        onClick={() => setActiveTool("pointer")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
          <path d="M13 13l6 6" />
        </svg>
      </ToolButton>
      <ToolButton
        label="Tiles (2, T)"
        active={activeTool === "tile-brush"}
        onClick={onTileBrushClick}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18.37 2.63 14 7l-1.59-1.59a2 2 0 0 0-2.82 0L8 7l9 9 1.59-1.59a2 2 0 0 0 0-2.82L17 10l4.37-4.37a2.12 2.12 0 1 0-3-3Z" />
          <path d="M9 8c-2 3-4 3.5-7 4l8 10c2-1 6-5 6-7" />
          <path d="M14.5 17.5 4.5 15" />
        </svg>
      </ToolButton>
      <ToolButton
        label="Decorations (3, D)"
        active={activeTool === "decoration-brush"}
        onClick={onDecorationBrushClick}
      >
        <TreePine size={18} strokeWidth={2} />
      </ToolButton>
      <ToolButton
        label="Eyedropper (4, E)"
        active={activeTool === "eyedropper"}
        onClick={() => setActiveTool("eyedropper")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m2 22 1-1h3l9-9" />
          <path d="M3 21v-3l9-9" />
          <path d="m15 6 3.4-3.4a2.1 2.1 0 1 1 3 3L18 9l.4.4a2.1 2.1 0 1 1-3 3l-3.8-3.8a2.1 2.1 0 1 1 3-3L15 6" />
        </svg>
      </ToolButton>
    </div>
  );
}

function EnrichTools({
  cloudSignedIn,
}: {
  cloudSignedIn: boolean;
}) {
  const activeTool = useEditorStore((s) => s.activeTool);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);

  return (
    <div className="flex items-center gap-1">
      <ToolButton
        label="Pointer (1, V)"
        active={activeTool === "pointer"}
        onClick={() => setActiveTool("pointer")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
          <path d="M13 13l6 6" />
        </svg>
      </ToolButton>
      <ToolButton
        label="Text (2, T)"
        active={activeTool === "text"}
        onClick={() => setActiveTool("text")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="4 7 4 4 20 4 20 7" />
          <line x1="9" y1="20" x2="15" y2="20" />
          <line x1="12" y1="4" x2="12" y2="20" />
        </svg>
      </ToolButton>
      <ToolButton
        label="Map Note (3, P)"
        active={activeTool === "map-note"}
        onClick={() => setActiveTool("map-note")}
        disabled={!cloudSignedIn}
        showDisabledCursor={cloudSignedIn}
        disabledTooltip={
          !cloudSignedIn
            ? "Sign in to start adding notes to your map"
            : undefined
        }
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      </ToolButton>
    </div>
  );
}

function getLastUsedBrushThumbnail(
  lastUsedBrush: LastUsedBrush,
): { url: string; label: string; isDecoration: boolean } | null {
  if (lastUsedBrush.kind === "tile") {
    const tileSet = getTileSetById(lastUsedBrush.tileSetId);
    if (!tileSet || tileSet.tileIds.length === 0) return null;
    const url = getTileUrl(tileSet.tileIds[0]);
    if (!url) return null;
    return { url, label: tileSet.label, isDecoration: false };
  }

  const decoration = getCatalogDecorationById(lastUsedBrush.decorationId);
  if (!decoration?.url) return null;
  return { url: decoration.url, label: decoration.title, isDecoration: true };
}

function LastUsedBrushBox() {
  const theme = getMapChromeTheme();
  const lastUsedBrush = useEditorStore((s) => s.lastUsedBrush);
  const selectedTileSetId = useEditorStore((s) => s.selectedTileSetId);
  const selectedDecorationId = useEditorStore((s) => s.selectedDecorationId);
  const selectBrush = useEditorStore((s) => s.selectBrush);
  const selectDecoration = useEditorStore((s) => s.selectDecoration);

  if (!lastUsedBrush) return null;

  if (
    lastUsedBrush.kind === "tile" &&
    selectedTileSetId === lastUsedBrush.tileSetId
  )
    return null;
  if (
    lastUsedBrush.kind === "decoration" &&
    selectedDecorationId === lastUsedBrush.decorationId
  )
    return null;

  const thumb = getLastUsedBrushThumbnail(lastUsedBrush);
  if (!thumb) return null;

  const tooltipLabel = `Pick up last brush: ${thumb.label}`;

  return (
    <button
      type="button"
      onClick={() => {
        if (lastUsedBrush.kind === "tile") {
          selectBrush(lastUsedBrush.tileSetId);
        } else {
          selectDecoration(
            lastUsedBrush.decorationId,
            lastUsedBrush.taxonomyNodeId,
          );
        }
      }}
      aria-label={tooltipLabel}
      title={tooltipLabel}
      className={`group relative h-9 w-9 shrink-0 overflow-hidden rounded-lg transition-all hover:scale-105 ${theme.actionButtonIdle}`}
    >
      <img
        src={thumb.url}
        alt=""
        aria-hidden="true"
        className={`h-full w-full opacity-40 transition-opacity group-hover:opacity-60 ${
          thumb.isDecoration ? "object-contain p-0.5" : "object-cover"
        }`}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center text-neutral-950/70 transition-colors group-hover:text-neutral-950"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 14 4 9l5-5" />
          <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
        </svg>
      </span>
      <span
        className={`pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 transition-opacity group-hover:opacity-100 ${theme.tooltip}`}
      >
        {tooltipLabel}
      </span>
    </button>
  );
}

export default function BottomToolbar({
  onTileBrushClick,
  onDecorationBrushClick,
  cloudSignedIn,
}: {
  onTileBrushClick?: () => void;
  onDecorationBrushClick?: () => void;
  cloudSignedIn: boolean;
}) {
  const theme = getMapChromeTheme();
  const editorMode = useEditorStore((s) => s.editorMode);
  const setEditorMode = useEditorStore((s) => s.setEditorMode);

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
      <div className={`flex flex-col items-center px-2 py-1.5 ${theme.floatingPanelCompact}`}>
        <div className={theme.switcherTrack}>
          <span
            data-active-tab-indicator
            className={theme.switcherIndicator}
            style={{
              transform: `translateX(${editorMode === "paint" ? "0%" : "100%"})`,
            }}
          />
          <TabButton
            label="Paint"
            active={editorMode === "paint"}
            onClick={() => setEditorMode("paint")}
          />
          <TabButton
            label="Enrich"
            active={editorMode === "enrich"}
            onClick={() => setEditorMode("enrich")}
          />
        </div>
          {editorMode === "paint" ? (
            <PaintTools
              onTileBrushClick={onTileBrushClick ?? (() => {})}
              onDecorationBrushClick={onDecorationBrushClick ?? (() => {})}
            />
          ) : (
          <EnrichTools cloudSignedIn={cloudSignedIn} />
        )}
      </div>
      {editorMode === "paint" && <LastUsedBrushBox />}
    </div>
  );
}
